import { useContext, useEffect, useState } from "react";
import { Card, CardMedia, Typography, Box, Chip } from "@mui/material";

import { Shirt } from "../../entities/Shirt";
import { LegitCheckItem } from "../../types/types";

import PropertyChips from "../PropertyChips/PropertyChips";
import UserShirtHeader from "./UserShirtHeader";
import ShirtDetail from "../ShirtDetail/ShirtDetail";
import LegitCheckResults from "../LegitCheckResults/LegitCheckResults";

import DefaultShirt from "../../img/defaultShirt.png";

import DialogService from "../Dialog/dialogService";
import FormatService from "../../services/formatService";
import LegitCheckService from "../../services/legitCheckService";

import { AbilityContext } from "../../permission/Can";
import { Actions, Subjects } from "../../permission/constants";

interface SquareViewProps {
  shirt: Shirt;
  imgHeight?: number;
  shouldShowUser?: boolean;
}

const SquareView = ({
  shirt,
  imgHeight = 75,
  shouldShowUser,
}: SquareViewProps) => {
  const { openMD } = DialogService();
  const { getMoneyFormat } = FormatService();

  const ability = useContext(AbilityContext);

  const shirtDetails = () => {
    if (shirt.owner) {
      openMD(
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <UserShirtHeader user={shirt.owner} />
        </Box>,
        <ShirtDetail shirt={shirt} />,
        [],
        true
      );
    }
  };

  const legitCheckResults = (event: any) => {
    if (shirt?.id) {
      openMD(
        "Legit Check Results",
        <LegitCheckResults itemId={shirt.id} />,
        []
      );
    }
    event.stopPropagation();
  };

  return (
    <Box sx={{ position: "relative", width: "100%", paddingTop: "160%" }}>
      <Card
        className={"shadow-box shadow-box--hover"}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          padding: "1rem",
          borderRadius: "10px",
          cursor: "pointer",
        }}
        aria-label="CardContainer"
      >
        {shouldShowUser && shirt.owner ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: { xs: "center", sm: "flex-start" },
              height: "10%",
            }}
            aria-label="CardContent"
          >
            <UserShirtHeader user={shirt.owner} />
          </Box>
        ) : (
          <></>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            height: `${imgHeight}%`,
            position: "relative",
          }}
          onClick={shirtDetails}
        >
          {shirt?.images[0]?.url ? (
            <CardMedia
              component="img"
              image={shirt.images[0].url}
              alt={shirt.title}
              style={{
                objectFit: "cover",
                objectPosition: "50% 50%",
                borderRadius: "10px",
              }}
              aria-label="CardMedia"
            />
          ) : (
            <CardMedia
              component="img"
              image={DefaultShirt}
              alt={shirt.title}
              style={{
                objectFit: "cover",
                objectPosition: "50% 50%",
                borderRadius: "10px",
              }}
              aria-label="CardMedia"
            />
          )}

          {shirt.current_price && shirt.current_price > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: "20px",
                left: "10px",
                width: "calc(100% - 20px)",
                color: "#3c3c40",
              }}
            >
              <Chip
                label={
                  <Typography sx={{ fontWeight: "700", fontStyle: "italic" }}>
                    {getMoneyFormat(shirt.current_price)}
                  </Typography>
                }
                sx={{ backgroundColor: "#ffffff", color: "#3c3c40" }}
              />
            </Box>
          )}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: "10px",
              width: "calc(100% - 20px)",
              color: "white",
              paddingBottom: "15px",
            }}
          >
            <PropertyChips shirt={shirt} />
          </Box>
        </Box>
        <Box
          sx={{
            height: shirt.owner
              ? `calc(90% - ${imgHeight}%)`
              : `calc(100% - ${imgHeight}%)`,
            paddingTop: "10px",
          }}
        >
          <Typography
            gutterBottom
            variant="body2"
            sx={{
              color: "#3c3c40",
              fontWeight: "700",
              fontSize: { xs: "17px", md: "16px" },
              marginBottom: "0px !important",
              fontStyle: "italic",
            }}
          >
            {shirt.model.team.name} - {shirt.model.season.start_year}/
            {shirt.model.season.end_year}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#6f7d95",
              fontWeight: "400",
            }}
          >
            {shirt.model.player
              ? shirt.model.player.alias &&
                shirt.model.player.name !== shirt.model.player.alias
                ? shirt.model.player.name +
                  ' "' +
                  shirt.model.player.alias +
                  '"'
                : shirt.model.player.name
              : ""}
          </Typography>

          <Box
            sx={{
              position: "absolute",
              bottom: "1rem",
              right: "1rem",
              width: "fit-content",
            }}
          >
            {ability.can(Actions.VIEW, Subjects.LEGIT_CHECK) && (
              <Typography
                sx={{
                  padding: 0,
                  fontFamily: "Manrope",
                  textDecoration: "underline",
                  textUnderlineOffset: "3px",
                  cursor: "pointer",
                  fontSize: "14px",
                  marginTop: "4px",
                }}
                onClick={legitCheckResults}
              >
                LC (
                {shirt.legitCheckResults?.positive
                  ? shirt.legitCheckResults?.positive
                  : "0"}
                /
                {shirt.legitCheckResults?.total
                  ? shirt.legitCheckResults?.total
                  : "0"}
                )
              </Typography>
            )}
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default SquareView;
